import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDatabase, 
  faSpinner,
  faInfoCircle,
  faChartLine,
  faClock,
  faMoneyBillWave,
  faRocket,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import CacheMetrics from './CacheMetrics';
import '../../styles/components/dashboard/MetricasPage.css';

function MetricasPage({ user }) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simular carga inicial
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const tooltips = {
    cacheHits: {
      title: "Aciertos de Caché",
      explanation: "Cantidad de veces que el sistema encontró la información solicitada en memoria rápida, evitando consultas lentas a la base de datos. Mayor porcentaje = mejor rendimiento."
    },
    cacheMisses: {
      title: "Fallos de Caché", 
      explanation: "Veces que el sistema tuvo que buscar información directamente en la base de datos porque no estaba en memoria. Menor cantidad = mejor eficiencia."
    },
    savings: {
      title: "Ahorro en Costos",
      explanation: "Dinero ahorrado en pesos chilenos gracias al sistema de caché inteligente. Esto reduce los costos de Firebase al evitar consultas innecesarias."
    },
    responseTime: {
      title: "Tiempo de Respuesta",
      explanation: "Qué tan rápido responde el sistema a las solicitudes. Menor tiempo = mejor experiencia para los usuarios."
    },
    dataSize: {
      title: "Tamaño de Datos",
      explanation: "Cantidad total de información manejada por el sistema. Se mide en KB (kilobytes) o MB (megabytes)."
    },
    activeUsers: {
      title: "Usuarios Activos",
      explanation: "Número de personas usando el sistema actualmente. Incluye administradores y usuarios regulares."
    }
  };

  const TooltipIcon = ({ tooltipKey }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltip = tooltips[tooltipKey];

    return (
      <div className="tooltip-container">
        <FontAwesomeIcon 
          icon={faInfoCircle} 
          className="info-icon"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        />
        {showTooltip && (
          <div className="tooltip-popup">
            <h4>{tooltip.title}</h4>
            <p>{tooltip.explanation}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="metricas-loading">
        <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
        <h2>Cargando métricas de la base de datos...</h2>
        <p>Analizando rendimiento del sistema</p>
      </div>
    );
  }

  return (
    <div className="metricas-page">
      {/* Header Section */}
      <div className="metricas-header">
        <div className="header-content">
          <div className="title-section">
            <FontAwesomeIcon icon={faDatabase} className="page-icon" />
            <div>
              <h1>Métricas de Base de Datos</h1>
              <p>Monitoreo en tiempo real del rendimiento del sistema</p>
            </div>
          </div>
          
          <div className="status-badge">
            <FontAwesomeIcon icon={faShieldAlt} />
            <span>Sistema Activo</span>
          </div>
        </div>
      </div>

      {/* Información para usuarios no técnicos */}
      <div className="info-section">
        <div className="info-card">
          <FontAwesomeIcon icon={faInfoCircle} className="info-icon-large" />
          <div className="info-content">
            <h3>¿Qué son estas métricas?</h3>
            <p>
              Esta página muestra el rendimiento de nuestro sistema en términos simples. 
              Las métricas nos ayudan a mantener el sistema rápido, eficiente y económico.
            </p>
            <div className="info-tips">
              <div className="tip">
                <FontAwesomeIcon icon={faRocket} />
                <span><strong>Verde</strong> = Excelente rendimiento</span>
              </div>
              <div className="tip">
                <FontAwesomeIcon icon={faClock} />
                <span><strong>Amarillo</strong> = Rendimiento normal</span>
              </div>
              <div className="tip">
                <FontAwesomeIcon icon={faMoneyBillWave} />
                <span><strong>Rojo</strong> = Necesita atención</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Métricas con tooltips */}
      <div className="metricas-content">
        <div className="metrics-wrapper">
          <div className="section-header">
            <h2>
              <FontAwesomeIcon icon={faChartLine} />
              Rendimiento del Sistema
            </h2>
            <p>Estadísticas detalladas con explicaciones para entender mejor el funcionamiento</p>
          </div>

          {/* Inyectar tooltips en el componente CacheMetrics */}
          <div className="enhanced-cache-metrics">
            <CacheMetrics 
              user={user} 
              tooltipComponent={TooltipIcon}
              tooltips={tooltips}
            />
          </div>
        </div>
      </div>

      {/* Glosario simple */}
      <div className="glosario-section">
        <h3>
          <FontAwesomeIcon icon={faInfoCircle} />
          Glosario de Términos
        </h3>
        <div className="glosario-grid">
          <div className="glosario-item">
            <h4>Caché</h4>
            <p>Memoria rápida que guarda información frecuentemente usada para acelerar el sistema.</p>
          </div>
          <div className="glosario-item">
            <h4>Base de Datos</h4>
            <p>Lugar donde se almacena toda la información de certificados y usuarios de forma permanente.</p>
          </div>
          <div className="glosario-item">
            <h4>Consultas</h4>
            <p>Peticiones que el sistema hace para buscar información específica.</p>
          </div>
          <div className="glosario-item">
            <h4>Optimización</h4>
            <p>Mejoras al sistema para hacerlo más rápido y usar menos recursos.</p>
          </div>
          <div className="glosario-item">
            <h4>Firebase</h4>
            <p>Servicio de Google que aloja nuestra base de datos en la nube.</p>
          </div>
          <div className="glosario-item">
            <h4>TTL</h4>
            <p>Tiempo de vida de la información en caché antes de actualizarse automáticamente.</p>
          </div>
        </div>
      </div>

      {/* Footer informativo */}
      <div className="metricas-footer">
        <div className="footer-content">
          <div className="footer-info">
            <FontAwesomeIcon icon={faShieldAlt} />
            <div>
              <h4>Sistema Seguro y Optimizado</h4>
              <p>Todas las métricas se actualizan automáticamente cada 30 segundos</p>
            </div>
          </div>
          <div className="last-update">
            Última actualización: {new Date().toLocaleString('es-CL')}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MetricasPage;